/**
 * Milestone 1 streaming UX master experiment eligibility check.
 *
 * `platform_editor_ai_streaming_ux_experience_m1` is the cohort control for the M1
 * streaming UX. It directly controls quick actions block streaming, streaming grey-out
 * suppression and quick actions agent attribution. Other M1 slices may apply additional
 * eligibility checks while remaining within the same cohort.
 *
 * Callers must pick the right variant:
 *
 * - `isStreamingUxExperienceM1Enabled` logs an experiment exposure. Use it only at
 *   action-driven decision points and after any prerequisite checks so only users who can
 *   receive the treatment are enrolled.
 * - `isStreamingUxExperienceM1EnabledNoExposure` logs nothing. Use it on render paths,
 *   where the canonical exposure has already been logged by an action-driven call site.
 *
 * Both variants default to `false`, so an absent or uninitialised experiment value leaves
 * M1 off rather than exposing the UX unexpectedly.
 */
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';

/**
 * Switcheroo key for the Milestone 1 streaming UX master experiment.
 *
 * Exported so tests and callers reference a single source of truth for the experiment name.
 */
export const STREAMING_UX_EXPERIENCE_M1 = 'platform_editor_ai_streaming_ux_experience_m1';

/**
 * Exposure-logging variant. Evaluate after prerequisite checks - see file-level JSDoc.
 */
export const isStreamingUxExperienceM1Enabled = (): boolean =>
	isExperimentEnabled(STREAMING_UX_EXPERIENCE_M1);

/**
 * Non-exposure-logging variant for render paths - see file-level JSDoc.
 */
export const isStreamingUxExperienceM1EnabledNoExposure = (): boolean =>
	UNSAFE_expValNoExposure(STREAMING_UX_EXPERIENCE_M1, 'isEnabled', false);
